import {
  type RefCallback,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { type MotionValue, useMotionValue, useTransform } from 'motion/react'
import { useComposedRefs } from '@radix-ui/react-compose-refs'
import {
  BehaviorSubject,
  distinctUntilChanged,
  fromEvent,
  map,
  merge,
  Subject,
} from 'rxjs'
import { useSubject } from '@/hooks/rx-hooks'
import { useCallbackRef } from '@/hooks/ref-hooks'

/**
 * Props for {@link useElementSize}.
 */
export interface ElementSizeProps {
  /**
   * Callback when the size changes.
   * @param size
   */
  onChange?: (size: ElementSize) => unknown
}

export interface ElementSizeResult {
  /**
   * Callback ref, allowing the size to react to changes to the element.
   * @param element
   */
  connectRef: RefCallback<Element>
  /**
   * The calculated size.
   * May be zeroed out.
   */
  size: ElementSize
  /**
   * The calculated size as a stream.
   */
  size$: BehaviorSubject<ElementSize>
  /**
   * The size as motion values.
   */
  sizeMotionValues: { width: MotionValue<number>; height: MotionValue<number> }
}

/**
 * Element size.
 */
export interface ElementSize {
  width: number
  height: number
}

/**
 * Get the calculated size of an element.
 */
export function useElementSize({
  onChange,
}: ElementSizeProps = {}): ElementSizeResult {
  const lastCalculatedSizeRef = useRef({
    width: 0,
    height: 0,
  })
  const [connectedElement, setConnectedElement] = useState<
    Element | null | undefined
  >(null)

  // These are used to prevent the layout effect from resetting the
  // size state whenever the element is swapped for another one.
  // This often happens when using Motion to animate between 2 states.
  const connectTrackerRef = useRef(0)
  const effectTrackerRef = useRef(0)
  const prevElementRef = useRef<Element | null | undefined>(null)

  const connectRef = useCallback((element: Element | null | undefined) => {
    const prev = prevElementRef.current
    prevElementRef.current = element

    // Whenever we connect a new element, we'll increase the tracker.
    const expected = ++connectTrackerRef.current
    setConnectedElement(element)

    if (!element) {
      // Legacy cleanup
      if (prev) {
        cleanup()
      }

      return
    }

    return cleanup

    function cleanup() {
      if (connectTrackerRef.current !== expected) {
        return
      }

      setConnectedElement(null)
    }
  }, [])

  const [size, setSizeState] = useState<ElementSize>(() => ({
    width: 0,
    height: 0,
  }))

  const widthMv = useMotionValue(size.width)
  const heightMv = useMotionValue(size.height)
  // This makes it so attaching the width and height to `style` of a motion
  // component won't sync the computed height back to the motion value.
  const readonlyWidthMv = useTransform(() => widthMv.get())
  const readonlyHeightMv = useTransform(() => heightMv.get())
  const sizeMotionValues = useMemo(
    () => ({ width: readonlyWidthMv, height: readonlyHeightMv }),
    [readonlyWidthMv, readonlyHeightMv],
  )

  const size$ = useSubject(() => new BehaviorSubject<ElementSize>(size))

  const setSize = useCallback(
    (size: ElementSize) => {
      setSizeState(size)
      size$.next(size)
      widthMv.set(size.width)
      heightMv.set(size.height)
      lastCalculatedSizeRef.current = size
    },
    [size$, widthMv, heightMv],
  )

  useLayoutEffect(() => {
    const element = connectedElement
    if (element) {
      // Any time we have a new element, copy over the current
      // "connect" tracker number.
      effectTrackerRef.current = connectTrackerRef.current
    }

    if (!element) {
      // Only reset the size if this effect corresponds to the most
      // recent element that was connected. That's how we know
      // that there hasn't been another element that supersedes the one
      // we're currently running cleanup for.
      if (effectTrackerRef.current !== connectTrackerRef.current) {
        return
      }

      setSize({
        width: 0,
        height: 0,
      })
      return
    }

    const rect = element.getBoundingClientRect()
    if (
      lastCalculatedSizeRef.current.width !== rect.width ||
      lastCalculatedSizeRef.current.height !== rect.height
    ) {
      setSize({
        width: rect.width,
        height: rect.height,
      })
    }

    function handler(size: ElementSize) {
      setSize(size)
      onChange?.(size)
    }

    SharedResizeObserver.instance.subscribe(element, handler)
    return () => {
      SharedResizeObserver.instance.unsubscribe(element, handler)
    }
  }, [connectedElement, onChange, setSize])

  return {
    size,
    size$,
    connectRef,
    sizeMotionValues,
  }
}

/**
 * Result for {@link useScrollOffsets}.
 */
export interface UseScrollOffsetsResult {
  /**
   * Callback ref, allowing the size to react to changes to the element.
   * @param element
   */
  connectRef: RefCallback<Element>

  /**
   * A motion value for the scroll position relative to the scroll start on the x-axis.
   * That is, `0` represents the very start of the scroll port.
   */
  scrollXStart: MotionValue<number>

  /**
   * A motion value for the scroll position relative to the scroll end on the x-axis.
   * That is, `0` represents the very end of the scroll port.
   */
  scrollXEnd: MotionValue<number>

  /**
   * A motion value for the scroll position relative to the scroll start on the y-axis.
   * That is, `0` represents the very start of the scroll port.
   */
  scrollYStart: MotionValue<number>

  /**
   * A motion value for the scroll position relative to the scroll end on the y-axis.
   * That is, `0` represents the very end of the scroll port.
   */
  scrollYEnd: MotionValue<number>

  /**
   * Allows the consumer to trigger a re-read of the scroll values.
   */
  computeScrollValues(): void
}

/**
 * Similar to Motion's `useScroll` but tracks relative offsets rather
 * than percentage-based progress for the scrolling element.
 */
export function useScrollOffsets(): UseScrollOffsetsResult {
  const connectedElementRef = useRef<Element>(null)

  const scrollXStart = useMotionValue(0)
  const scrollXEnd = useMotionValue(0)
  const scrollYStart = useMotionValue(0)
  const scrollYEnd = useMotionValue(0)

  const updateScrollValues = useCallback(
    (element: Element) => {
      const {
        scrollTop,
        scrollLeft,
        scrollHeight,
        scrollWidth,
        clientHeight,
        clientWidth,
      } = element

      const scrollBottom = clientHeight + scrollTop
      scrollYStart.set(scrollTop)
      scrollYEnd.set(Math.round(scrollHeight - scrollBottom))

      const scrollRight = clientWidth + scrollLeft
      scrollXStart.set(scrollLeft)
      scrollXEnd.set(Math.round(scrollWidth - scrollRight))
    },
    [scrollXStart, scrollXEnd, scrollYStart, scrollYEnd],
  )

  const computeScrollValues = useCallback(() => {
    if (!connectedElementRef.current) {
      return
    }
    updateScrollValues(connectedElementRef.current)
  }, [updateScrollValues])

  const connectRef = useCallbackRef(
    useCallback(
      (element: Element) => {
        const scrollHandler = () => updateScrollValues(element)

        // If the element being tracked is the `documentElement`, we'll need to add
        // the listener on the actual document instead. Not sure why.
        const target = element === document.documentElement ? document : element
        target.addEventListener('scroll', scrollHandler)
        connectedElementRef.current = element

        // Read immediately
        updateScrollValues(element)

        return () => {
          if (connectedElementRef.current === element) {
            connectedElementRef.current = null
          }
          target.removeEventListener('scroll', scrollHandler)
        }
      },
      [updateScrollValues],
    ),
  )

  // Recalculate the scroll values when the element size changes.
  const { connectRef: connectScrollRef } = useElementSize({
    onChange: computeScrollValues,
  })

  const mergedRef = useComposedRefs(connectRef, connectScrollRef)

  return {
    connectRef: mergedRef,
    scrollXStart,
    scrollXEnd,
    scrollYStart,
    scrollYEnd,
    computeScrollValues,
  }
}

/**
 * Result for {@link useIsElementInView}.
 */
export interface UseIsElementInViewResult {
  /**
   * Callback ref for the element to observe.
   */
  connectRef: RefCallback<Element>
  /**
   * Whether the element intersects the viewport.
   */
  isInView: boolean
}

/**
 * Observes whether the element is in view.
 *
 * @param onChange Called when the element enters or leaves the viewport.
 */
export function useIsElementInView(
  onChange?: (isInView: boolean) => unknown,
): UseIsElementInViewResult {
  const [isInView, setIsInView] = useState(false)
  const connectRef = useCallbackRef(
    useCallback(
      (element: Element) => {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            setIsInView(entry.isIntersecting)
            onChange?.(entry.isIntersecting)
          })
        })
        observer.observe(element)
        return () => {
          observer.disconnect()
        }
      },
      [onChange],
    ),
  )
  return { connectRef, isInView }
}

/**
 * Result for {@link useHoverState}.
 */
export interface UseHoverStateResult {
  /**
   * Callback ref for the element to track.
   */
  connectRef: RefCallback<Element>
  /**
   * Whether the pointer is over the element.
   */
  isHovering: boolean
}

/**
 * Tracks whether the pointer is over the element.
 *
 * @param onHoverChange Called when the hover state changes.
 */
export function useHoverState(
  onHoverChange?: (isHovering: boolean) => unknown,
): UseHoverStateResult {
  const [isHovering, setIsHovering] = useState(false)
  const connectRef = useCallbackRef(
    useCallback(
      (element: Element) => {
        const disconnecting = new Subject<boolean>()
        const subscription = merge(
          fromEvent(element, 'mouseenter').pipe(map(() => true)),
          fromEvent(element, 'mouseleave').pipe(map(() => false)),
          disconnecting,
        )
          .pipe(distinctUntilChanged())
          .subscribe((value) => {
            setIsHovering(value)
            onHoverChange?.(value)
          })
        return () => {
          disconnecting.next(false)
          subscription.unsubscribe()
        }
      },
      [onHoverChange],
    ),
  )
  return { connectRef, isHovering }
}

/**
 * Signature for a handler that is called when the size of an element changes.
 */
type SizeChangedHandler = (size: ElementSize) => void

/**
 * Wrapper around a resize observer.
 * This is more performant than creating observers per element.
 */
class SharedResizeObserver {
  /**
   * Shared instance.
   */
  static readonly instance = new SharedResizeObserver()

  /**
   * Internal set of subscriptions.
   *
   * @private
   */
  private _subscriptions = new Map<Element, Set<SizeChangedHandler>>()

  /**
   * The shared resize observer.
   *
   * @private
   */
  private _observer = new ResizeObserver(this._onResize.bind(this))

  constructor() {
    const vv = 'visualViewport' in window && window.visualViewport
    if (vv) {
      vv.addEventListener('resize', this._onVirtualViewportResize.bind(this))
    }
  }

  /**
   * Adds a callback to the element's subscription list.
   * @param element
   * @param callback
   */
  subscribe(element: Element, callback: SizeChangedHandler) {
    let set = this._subscriptions.get(element)
    if (!set) {
      set = new Set()
      this._subscriptions.set(element, set)
      // Border box, so resizes report the same size as the initial
      // `getBoundingClientRect()` read.
      this._observer.observe(element, { box: 'border-box' })
    }

    set.add(callback)
  }

  /**
   * Removes the callback from the element's subscription list.
   *
   * @param element
   * @param callback
   */
  unsubscribe(element: Element, callback: SizeChangedHandler) {
    const set = this._subscriptions.get(element)
    if (!set) {
      return
    }

    set.delete(callback)
    if (set.size === 0) {
      this._subscriptions.delete(element)
      this._observer.unobserve(element)
    }
  }

  /**
   * Resize handler.
   *
   * @param entries
   */
  private _onResize(entries: ResizeObserverEntry[]) {
    for (const entry of entries) {
      const borderBoxSize = entry.borderBoxSize[0]
      const size: ElementSize = {
        width: borderBoxSize.inlineSize,
        height: borderBoxSize.blockSize,
      }

      const handlers = this._subscriptions.get(entry.target)
      if (handlers) {
        for (const handler of handlers) {
          handler(size)
        }
      }
    }
  }

  /**
   * Triggered when the virtual viewport resizes which can sometimes
   * lead to bad calculations, so we re-read the sizes when that happens.
   *
   * This works around an issue where after the virtual keyboard on
   * Mobile Safari is dismissed, the top offsets calculated from these sizes
   * are wrong.
   *
   * @private
   */
  private _onVirtualViewportResize() {
    requestAnimationFrame(() => {
      for (const [element, handlers] of this._subscriptions.entries()) {
        const rect = element.getBoundingClientRect()
        const size: ElementSize = {
          width: rect.width,
          height: rect.height,
        }
        for (const handler of handlers) {
          handler(size)
        }
      }
    })
  }
}

/**
 * Result of {@link useFittingItems}.
 */
export interface FittingItemsResult<T> {
  /**
   * The row the items have to fit in.
   */
  rowRef: RefCallback<HTMLElement>
  /**
   * An off-screen copy of the row holding every item at its natural width,
   * followed by the button the rest fold into.
   */
  measureRef: RefCallback<HTMLElement>
  /**
   * The items that fit, in order.
   */
  shown: T[]
  /**
   * The items that don't, for the button to offer.
   */
  folded: T[]
}

/**
 * Which of a row's items fit its width, leaving room for the button the rest
 * fold into when they don't all fit. The item at `pinnedIndex` always stays
 * in the row, in place of the last one that would have fit. With
 * `hasButton`, room is kept for the button even when every item fits.
 */
export function useFittingItems<T>(
  items: T[],
  pinnedIndex = -1,
  hasButton = false,
): FittingItemsResult<T> {
  const itemCount = items.length
  const [row, setRow] = useState<HTMLElement | null>(null)
  const [measure, setMeasure] = useState<HTMLElement | null>(null)
  const [count, setCount] = useState(itemCount)

  useLayoutEffect(() => {
    if (!row || !measure) {
      return
    }
    const update = () => {
      const children = Array.from(measure.children) as HTMLElement[]
      const widths = children.slice(0, itemCount).map((c) => c.offsetWidth)
      const button = children[itemCount]?.offsetWidth ?? 0
      const gap = parseFloat(getComputedStyle(measure).columnGap) || 0
      const available = row.clientWidth
      // The first `n` items, or the first `n - 1` and the pinned one when it
      // comes later.
      const widthOf = (n: number) => {
        const fitting =
          pinnedIndex >= n
            ? [...widths.slice(0, n - 1), widths[pinnedIndex]]
            : widths.slice(0, n)
        return fitting.reduce((sum, w) => sum + w, 0) + gap * (n - 1)
      }

      let fits = itemCount
      while (
        fits > 0 &&
        widthOf(fits) + (hasButton || fits < itemCount ? gap + button : 0) >
          available
      ) {
        fits--
      }
      setCount(fits)
    }
    update()
    SharedResizeObserver.instance.subscribe(row, update)
    SharedResizeObserver.instance.subscribe(measure, update)
    return () => {
      SharedResizeObserver.instance.unsubscribe(row, update)
      SharedResizeObserver.instance.unsubscribe(measure, update)
    }
  }, [row, measure, itemCount, pinnedIndex, hasButton])

  const shown =
    pinnedIndex >= count && count > 0
      ? [...items.slice(0, count - 1), items[pinnedIndex]]
      : items.slice(0, count)
  const folded = items.filter((item) => !shown.includes(item))
  return { rowRef: setRow, measureRef: setMeasure, shown, folded }
}
